import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, Map, BookOpen, MessageCircle, Sprout, Scan, Settings, LogOut, Cloud, Droplets,
  TrendingUp, FlaskConical, GraduationCap, Crown, BarChart3, Bug, Bell, Target, Calendar,
  CalendarRange, Trophy, Users, DollarSign, Newspaper, Package, Star, Cpu, ChefHat,
  ChevronDown, ChevronRight, Menu, X, Sparkles, Lock, LayoutGrid,
  PanelLeftClose, PanelLeftOpen, Pencil, Plus, ArrowUp, ArrowDown, Video, Receipt, MoreHorizontal,
} from 'lucide-react';
import axios from 'axios';

import VitalsHud from '@/components/VitalsHud';
import CoachDock from '@/components/CoachDock';
import PremiumGate from '@/components/PremiumGate';
import { isMember, PREMIUM_NAV_PATHS } from '@/lib/entitlements';
import { clearCache } from '@/lib/offlineCache';
import { FEATURES } from '@/config/features';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Maps the English nav label (as defined in the registry above) → its i18n key, so the
// sidebar/rail/menu translate at render without editing every registry object.
const NAV_I18N = {
  'Today': 'nav.today', 'Handbook': 'nav.handbook', 'My Crops': 'nav.myCrops', 'Coach': 'nav.coach',
  'Community': 'nav.community', 'Garden': 'nav.garden', 'Week Plan': 'nav.weekPlan', 'Season': 'nav.season',
  'Season Plan': 'nav.seasonPlan', 'Pantry': 'nav.pantry', 'Weather': 'nav.weather', 'Watering': 'nav.watering',
  'Soil': 'nav.soil', 'Soil & Fertility': 'nav.soilFertility', 'Scanner': 'nav.scanner', 'AI Scanner': 'nav.aiScanner',
  'Pest Log': 'nav.pestLog', 'Harvests': 'nav.harvests', 'Analytics': 'nav.analytics', 'Yield Analytics': 'nav.yieldAnalytics',
  'Value': 'nav.value', 'Garden Value': 'nav.gardenValue', 'Progress': 'nav.progress', 'Skills': 'nav.skills',
  'Your Skills': 'nav.yourSkills', 'Learning': 'nav.learning', 'Digest': 'nav.digest', "Grower's Digest": 'nav.growersDigest',
  'Varieties': 'nav.varieties', 'Recommended Varieties': 'nav.recommendedVarieties', 'Seed Box': 'nav.seedBox',
  'Shop': 'nav.shop', 'Boutique Shop': 'nav.boutiqueShop', 'Rewards': 'nav.rewards', 'Collective Rewards': 'nav.collectiveRewards',
  'Orders': 'nav.orders', 'My Orders': 'nav.myOrders', 'Videos': 'nav.videos', 'Founder Videos': 'nav.founderVideos',
  'Messages': 'nav.messages', 'Upgrade': 'nav.upgrade', 'Alerts': 'nav.alerts', 'Settings': 'nav.settings', 'Sign out': 'nav.signOut',
  'Planning': 'nav.planning', 'Intelligence': 'nav.intelligence', 'Results': 'nav.results', 'Account': 'nav.account',
  'Ask Coach': 'nav.askCoach', 'Log Harvest': 'nav.logHarvest', 'Scan Plant': 'nav.scanPlant', 'New Planting': 'nav.newPlanting',
  'Garden Planner': 'nav.garden', 'Season Planner': 'nav.seasonPlan',
};

const COACH_PAGES = ['/today', '/dashboard', '/planner'];

// ── Main-action focus (mirrors mobile nav-bar.tsx) — the big center button ──
const MAIN_ACTIONS = {
  new_planting: { key: 'new_planting', label: 'New Planting', Icon: Sprout, path: '/crops' },
  scanner: { key: 'scanner', label: 'Scan Plant', Icon: Scan, path: '/scanner' },
  coach: { key: 'coach', label: 'Ask Coach', Icon: MessageCircle, path: '/coach' },
  log_harvest: { key: 'log_harvest', label: 'Log Harvest', Icon: Sprout, path: '/my-crops' },
  community: { key: 'community', label: 'Community', Icon: Users, path: '/community' },
};
function resolveMainAction(key, aiOn, communityOn) {
  let k = key || 'new_planting';
  if (k === 'community' && !communityOn) k = 'new_planting';
  if (k === 'coach' && !aiOn) k = 'new_planting';
  return MAIN_ACTIONS[k] ? k : 'new_planting';
}

// ── Fully-customizable quick-tile pool (add as many as you like) ──
const QUICK_TILE_POOL = {
  today: { label: 'Today', icon: Home, path: '/today' },
  crops: { label: 'Handbook', icon: BookOpen, path: '/crops' },
  'my-crops': { label: 'My Crops', icon: Sprout, path: '/my-crops' },
  coach: { label: 'Coach', icon: MessageCircle, path: '/coach', aiOnly: true },
  community: { label: 'Community', icon: Users, path: '/community', communityOnly: true },
  planner: { label: 'Garden', icon: Map, path: '/planner' },
  'weekly-plan': { label: 'Week Plan', icon: Calendar, path: '/weekly-plan' },
  'season-plan': { label: 'Season', icon: CalendarRange, path: '/season-plan' },
  pantry: { label: 'Pantry', icon: ChefHat, path: '/pantry' },
  weather: { label: 'Weather', icon: Cloud, path: '/weather' },
  watering: { label: 'Watering', icon: Droplets, path: '/watering' },
  soil: { label: 'Soil', icon: FlaskConical, path: '/soil' },
  scanner: { label: 'Scanner', icon: Scan, path: '/scanner' },
  'pest-log': { label: 'Pest Log', icon: Bug, path: '/pest-log' },
  harvests: { label: 'Harvests', icon: TrendingUp, path: '/harvests' },
  analytics: { label: 'Analytics', icon: BarChart3, path: '/analytics' },
  value: { label: 'Value', icon: DollarSign, path: '/value' },
  progress: { label: 'Progress', icon: Trophy, path: '/progress' },
  skills: { label: 'Skills', icon: Target, path: '/skills' },
  learn: { label: 'Learning', icon: GraduationCap, path: '/learn' },
  digest: { label: 'Digest', icon: Newspaper, path: '/digest' },
  varieties: { label: 'Varieties', icon: Star, path: '/varieties' },
  'seed-box': { label: 'Seed Box', icon: Package, path: '/seed-box' },
  orders: { label: 'Orders', icon: Receipt, path: '/orders' },
  videos: { label: 'Videos', icon: Video, path: '/videos' },
  messages: { label: 'Messages', icon: MessageCircle, path: '/messages' },
  subscription: { label: 'Upgrade', icon: Crown, path: '/paywall' },
  notifications: { label: 'Alerts', icon: Bell, path: '/notifications' },
};
if (!FEATURES.seedBox) delete QUICK_TILE_POOL['seed-box'];
if (!FEATURES.pantry) delete QUICK_TILE_POOL['pantry'];
const DEFAULT_QUICK_TILES = ['today', 'crops', 'my-crops'];

// ── Stacked Hubs (Planning before Intelligence per preference) ──
const HUBS = [
  {
    key: 'planning', label: 'Planning', Icon: LayoutGrid, items: [
      { path: '/planner', label: 'Garden Planner', icon: Map, testId: 'nav-planner' },
      { path: '/season-plan', label: 'Season Planner', icon: CalendarRange, testId: 'nav-season-plan' },
      { path: '/weekly-plan', label: 'Week Plan', icon: Calendar, testId: 'nav-weekly' },
    ],
    more: [
      { path: '/calendar', label: 'Planting Calendar', icon: Calendar, testId: 'nav-calendar' },
      { path: '/seed-box', label: 'Seed Box', icon: Package, testId: 'nav-seed-box' },
      { path: '/pantry', label: 'Pantry', icon: ChefHat, testId: 'nav-pantry' },
    ],
  },
  {
    key: 'intelligence', label: 'Intelligence', Icon: Cpu, gated: true, items: [
      { path: '/weather', label: 'Weather', icon: Cloud, testId: 'nav-weather' },
      { path: '/watering', label: 'Watering', icon: Droplets, testId: 'nav-watering' },
      { path: '/soil', label: 'Soil & Fertility', icon: FlaskConical, testId: 'nav-soil' },
      { path: '/scanner', label: 'Scanner', icon: Scan, testId: 'nav-scanner' },
      { path: '/pest-log', label: 'Pest Log', icon: Bug, testId: 'nav-pest-log' },
    ],
  },
  {
    key: 'learning', label: 'Learning', Icon: GraduationCap, items: [
      { path: '/crops', label: 'Handbook', icon: BookOpen, testId: 'nav-crops-hub' },
      { path: '/learn', label: 'Learning', icon: GraduationCap, testId: 'nav-learn' },
      { path: '/skills', label: 'Your Skills', icon: Target, testId: 'nav-skills' },
      { path: '/videos', label: 'Founder Videos', icon: Video, testId: 'nav-videos' },
      { path: '/digest', label: "Grower's Digest", icon: Newspaper, testId: 'nav-digest' },
    ],
  },
  {
    key: 'results', label: 'Results', Icon: TrendingUp, items: [
      { path: '/harvests', label: 'Harvests', icon: TrendingUp, testId: 'nav-harvests' },
      { path: '/analytics', label: 'Yield Analytics', icon: BarChart3, testId: 'nav-analytics' },
      { path: '/value', label: 'Garden Value', icon: DollarSign, testId: 'nav-value' },
      { path: '/progress', label: 'Progress', icon: Trophy, testId: 'nav-progress' },
    ],
  },
  {
    key: 'account', label: 'Account', Icon: Receipt, items: [
      { path: '/paywall', label: 'Upgrade', icon: Crown, testId: 'nav-subscription' },
    ],
  },
];

function Layout({ children, user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t: tr } = useTranslation();
  const L = (label) => { const k = NAV_I18N[label]; return k ? tr(k) : label; };
  const [avatarError, setAvatarError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [wheelOpen, setWheelOpen] = useState(false);
  const [mainOverride, setMainOverride] = useState(null);
  const [wheelPos, setWheelPos] = useState({ top: 0, left: 0 });
  const wheelTimer = React.useRef(null);
  const wheelAnchorRef = React.useRef(null);

  const [railPinned, setRailPinned] = useState(() => {
    try { return localStorage.getItem('hg_rail_pinned') !== '0'; } catch { return true; }
  });
  const [railHover, setRailHover] = useState(false);
  const [openHubs, setOpenHubs] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('hg_hubs_open') || 'null');
      if (saved && typeof saved === 'object') return saved;
    } catch { /* ignore */ }
    return { planning: true, intelligence: true, learning: false, results: false, account: false };
  });
  const [coachOpen, setCoachOpen] = useState(() => {
    try { return localStorage.getItem('hg_coach_open') === '1'; } catch { return false; }
  });
  const [quickTiles, setQuickTiles] = useState(() => {
    const saved = user?.web_quick_tiles;
    if (Array.isArray(saved) && saved.length) return saved.filter((k) => QUICK_TILE_POOL[k]);
    return DEFAULT_QUICK_TILES;
  });

  const setCoach = (v) => {
    setCoachOpen(v);
    try { localStorage.setItem('hg_coach_open', v ? '1' : '0'); } catch { /* ignore */ }
  };
  const togglePin = () => {
    setRailPinned((p) => {
      const next = !p;
      try { localStorage.setItem('hg_rail_pinned', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };
  const toggleHub = (key) => {
    setOpenHubs((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('hg_hubs_open', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const [moreOpen, setMoreOpen] = useState({});
  const toggleMore = (key) => setMoreOpen((p) => ({ ...p, [key]: !p[key] }));

  const aiOn = user?.ai_coach_enabled !== false;
  const intelOn = user?.intelligence_enabled !== false;
  const communityOn = user?.community_enabled !== false;
  const member = isMember(user);
  const showCoach = aiOn && COACH_PAGES.includes(location.pathname);
  const expanded = railPinned || railHover;

  const mainKey = resolveMainAction(mainOverride || user?.main_action, aiOn, communityOn);
  const main = MAIN_ACTIONS[mainKey];
  const MainIcon = main.Icon;
  const wheelActions = Object.values(MAIN_ACTIONS).filter(
    (a) => !(a.key === 'coach' && !aiOn) && !(a.key === 'community' && !communityOn),
  );
  const pinMain = (k) => {
    setMainOverride(k);
    axios.patch(`${API}/users/me`, { main_action: k }, { withCredentials: true }).catch(() => {});
  };
  const openWheelSoon = () => { clearTimeout(wheelTimer.current); wheelTimer.current = setTimeout(() => { positionWheel(); setWheelOpen(true); }, 320); };
  const closeWheelSoon = () => { clearTimeout(wheelTimer.current); wheelTimer.current = setTimeout(() => setWheelOpen(false), 180); };
  const cancelWheelClose = () => clearTimeout(wheelTimer.current);
  const positionWheel = () => {
    const r = wheelAnchorRef.current?.getBoundingClientRect();
    if (r) setWheelPos({ top: r.top, left: r.right + 10 });
  };
  const toggleWheel = (e) => { e.preventDefault(); e.stopPropagation(); positionWheel(); setWheelOpen((o) => !o); };

  const tileVisible = (k) => {
    const t = QUICK_TILE_POOL[k];
    if (!t) return false;
    if (t.aiOnly && !aiOn) return false;
    if (t.communityOnly && !communityOn) return false;
    return true;
  };
  const visibleTiles = quickTiles.filter(tileVisible);
  const addableTiles = Object.keys(QUICK_TILE_POOL).filter((k) => !quickTiles.includes(k) && tileVisible(k));

  const saveTiles = (next) => {
    setQuickTiles(next);
    axios.patch(`${API}/users/me`, { web_quick_tiles: next }, { withCredentials: true }).catch(() => {});
  };
  const addTile = (k) => { if (!quickTiles.includes(k)) saveTiles([...quickTiles, k]); };
  const removeTile = (k) => saveTiles(quickTiles.filter((x) => x !== k));
  const moveTile = (k, dir) => {
    const i = quickTiles.indexOf(k);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= quickTiles.length) return;
    const next = [...quickTiles];
    [next[i], next[j]] = [next[j], next[i]];
    saveTiles(next);
  };

  React.useEffect(() => {
    axios.get(`${API}/notifications`, { withCredentials: true })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setUnread(list.filter((n) => !n.read).length);
      })
      .catch(() => {});
    axios.get(`${API}/messages/unread-count`, { withCredentials: true })
      .then((res) => setMsgUnread(res.data?.count || 0))
      .catch(() => {});
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      clearCache();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      clearCache();
      navigate('/login');
    }
  };

  const isActive = (path) => location.pathname === path;

  const HubItem = ({ item }) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    return (
      <Link to={item.path} data-testid={item.testId}
        className={`hg-press flex items-center gap-3 pl-11 pr-3 py-2 rounded-xl transition-all ${active
          ? 'bg-[#1A4331] text-white shadow-[0_8px_24px_-10px_rgba(26,67,49,0.7)]'
          : item.path === '/paywall'
            ? 'text-[#C9A85B] hover:bg-[#C9A85B]/10'
            : 'text-[#5C665C] hover:bg-white/60 hover:text-[#1A4331]'}`}>
        <Icon className="w-[18px] h-[18px] flex-shrink-0" />
        <span className="font-medium text-[14px] truncate">{L(item.label)}</span>
        {!member && PREMIUM_NAV_PATHS.includes(item.path) && (
          <Lock className="w-3.5 h-3.5 ml-auto text-[#C9A85B]" data-testid={`nav-lock-${item.testId}`} />
        )}
      </Link>
    );
  };

  const QuickBtn = ({ tileKey }) => {
    const t = QUICK_TILE_POOL[tileKey];
    if (!t) return null;
    const Icon = t.icon;
    const active = isActive(t.path);
    const cls = active
      ? 'bg-[#0F2D1F] text-[#B4D330]'
      : t.accent
        ? 'bg-[#C9A85B]/20 text-[#7A5E1F] ring-1 ring-[#C9A85B]/55 hover:bg-[#C9A85B]/30'
        : 'bg-white/55 text-[#1A4331] hover:bg-white/80';
    return (
      <Link to={t.path} data-testid={`quick-${tileKey}`}
        className={`hg-press relative flex items-center rounded-2xl transition-all ${expanded
          ? 'flex-col gap-1.5 px-1.5 py-2.5'
          : 'flex-col justify-center w-11 h-11 mx-auto'} ${cls}`}>
        {t.accent && !active && (
          <span className="absolute top-1 right-1.5 w-1.5 h-1.5 rounded-full bg-[#C9A85B]" />
        )}
        <Icon className="w-5 h-5 flex-shrink-0" />
        {expanded && <span className="font-semibold text-[11px] leading-tight truncate max-w-full">{L(t.label)}</span>}
      </Link>
    );
  };

  return (
    <div className="min-h-screen hg-canvas">
      {/* ─── MOBILE top bar ─── */}
      <nav className="md:hidden bg-[#FAF6EC] border-b border-[#1A4331]/10 sticky top-0 z-50">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="logo-link-mobile">
              <div className="w-10 h-10 bg-[#1A4331] rounded-full flex items-center justify-center">
                <Sprout className="w-6 h-6 text-[#B4D330]" />
              </div>
              <span className="text-2xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Homegrown</span>
            </Link>
            <div className="flex items-center gap-5">
              <Link to="/notifications" data-testid="nav-notifications-top" className="relative">
                <Bell className="w-5 h-5 text-[#5C665C]" />
                {unread > 0 && (
                  <span className="absolute -top-2 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-[#E2725B] text-white text-[10px] font-bold flex items-center justify-center">
                    {unread > 9 ? '9+' : unread}
                  </span>
                )}
              </Link>
              <Link to="/settings" data-testid="settings-link-mobile"><Settings className="w-5 h-5 text-[#5C665C]" /></Link>
              <button onClick={handleLogout} data-testid="logout-button-mobile"><LogOut className="w-5 h-5 text-[#5C665C]" /></button>
            </div>
          </div>
        </div>
      </nav>

      {/* ─── MOBILE bottom nav ─── */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-[#FAF6EC] border-t border-[#1A4331]/10 z-50">
        <div className="flex items-end justify-around px-2 pt-1 pb-2">
          {[
            { path: '/today', label: 'Today', icon: Home, testId: 'bottom-today' },
            { path: '/crops', label: 'Handbook', icon: BookOpen, testId: 'bottom-crops' },
          ].map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link key={item.path} to={item.path} data-testid={item.testId} className="flex flex-col items-center gap-1 px-2 py-1 flex-1">
                <Icon className={`w-5 h-5 ${active ? 'text-[#1A4331]' : 'text-[#5C665C]'}`} />
                <span className={`text-[11px] font-medium ${active ? 'text-[#1A4331]' : 'text-[#5C665C]'}`}>{L(item.label)}</span>
              </Link>
            );
          })}
          <Link to={main.path} data-testid="bottom-main-action" className="flex flex-col items-center flex-1 -mt-6">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg ring-4 ring-[#FAF6EC] transition-all ${isActive(main.path) ? 'bg-[#0F2D1F]' : 'bg-[#1A4331]'}`}>
              <MainIcon className="w-6 h-6 text-[#B4D330]" />
            </div>
            <span className="text-[11px] font-semibold text-[#1A4331] mt-0.5">{L(main.label)}</span>
          </Link>
          {communityOn ? (
            <Link to="/community" data-testid="bottom-community" className="flex flex-col items-center gap-1 px-2 py-1 flex-1">
              <Users className={`w-5 h-5 ${isActive('/community') ? 'text-[#1A4331]' : 'text-[#5C665C]'}`} />
              <span className={`text-[11px] font-medium ${isActive('/community') ? 'text-[#1A4331]' : 'text-[#5C665C]'}`}>Community</span>
            </Link>
          ) : (
            <Link to="/my-crops" data-testid="bottom-my-crops" className="flex flex-col items-center gap-1 px-2 py-1 flex-1">
              <Sprout className={`w-5 h-5 ${isActive('/my-crops') ? 'text-[#1A4331]' : 'text-[#5C665C]'}`} />
              <span className={`text-[11px] font-medium ${isActive('/my-crops') ? 'text-[#1A4331]' : 'text-[#5C665C]'}`}>{L('My Crops')}</span>
            </Link>
          )}
          <button onClick={() => setMenuOpen(true)} data-testid="bottom-menu-toggle" className="flex flex-col items-center gap-1 px-2 py-1 flex-1">
            <Menu className="w-5 h-5 text-[#5C665C]" />
            <span className="text-[11px] font-medium text-[#5C665C]">{tr('nav.menu')}</span>
          </button>
        </div>
      </div>

      {/* ─── MOBILE full menu overlay ─── */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-[60] bg-[#0F2D1F]/40 backdrop-blur-sm" onClick={() => setMenuOpen(false)} data-testid="mobile-menu-overlay">
          <div className="absolute bottom-0 left-0 right-0 bg-[#FAF6EC] rounded-t-3xl max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <span className="text-lg font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{tr('nav.menu')}</span>
              <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close" className="w-9 h-9 rounded-full bg-[#F0F2EB] flex items-center justify-center">
                <X className="w-5 h-5 text-[#5C665C]" />
              </button>
            </div>
            {HUBS.filter((h) => !(h.gated && !intelOn)).map((hub) => (
              <div key={hub.key} className="mb-4">
                <p className="text-xs font-semibold text-[#5C665C] uppercase tracking-wider mb-2">{L(hub.label)}</p>
                <div className="grid grid-cols-2 gap-2">
                  {[...hub.items, ...(hub.more || [])].filter((it) => it.path !== '/community' || communityOn).map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <Link key={item.path} to={item.path} onClick={() => setMenuOpen(false)} data-testid={`menu-${item.testId}`} className={`flex items-center gap-2 px-3 py-3 rounded-xl ${active ? 'bg-[#1A4331] text-white' : 'bg-[#F0F2EB] text-[#2D241E]'}`}>
                        <Icon className="w-4 h-4" />
                        <span className="text-sm font-medium">{L(item.label)}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── DESKTOP PRO RAIL ─── */}
      <aside
        onMouseEnter={() => setRailHover(true)}
        onMouseLeave={() => setRailHover(false)}
        data-testid="pro-rail"
        className={`hidden md:flex fixed left-0 top-0 bottom-0 hg-glass-rail flex-col z-40 transition-[width] duration-300 ease-out ${expanded ? 'w-72' : 'w-[76px]'} ${railHover && !railPinned ? 'shadow-[0_24px_60px_-20px_rgba(15,45,31,0.5)]' : ''}`}
      >
        {/* Logo + pin toggle */}
        <div className={`flex items-center pt-5 pb-4 ${expanded ? 'px-5 justify-between' : 'px-0 justify-center'}`}>
          <Link to="/dashboard" className="flex items-center gap-3 hg-press" data-testid="logo-link">
            <div className="w-11 h-11 bg-[#1A4331] rounded-2xl flex items-center justify-center shadow-[0_8px_20px_-8px_rgba(26,67,49,0.8)] flex-shrink-0">
              <Sprout className="w-6 h-6 text-[#B4D330]" />
            </div>
            {expanded && <span className="text-2xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Homegrown</span>}
          </Link>
          {expanded && (
            <button onClick={togglePin} data-testid="rail-pin-toggle" aria-label="Collapse sidebar"
              className="hg-press w-8 h-8 rounded-lg hover:bg-white/60 flex items-center justify-center text-[#5C665C]">
              {railPinned ? <PanelLeftClose className="w-[18px] h-[18px]" /> : <PanelLeftOpen className="w-[18px] h-[18px]" />}
            </button>
          )}
        </div>

        {/* Quick Actions bar — main action (large) + customizable tiles */}
        <div className={`pb-3 max-h-[46vh] overflow-y-auto hg-scroll ${expanded ? 'px-3' : 'px-2'}`} data-testid="quick-actions">
          {expanded && (
            <div className="flex items-center justify-between px-2 mb-2">
              <p className="text-[11px] font-semibold text-[#5C665C] uppercase tracking-wider">{tr('nav.quickActions')}</p>
              <button onClick={() => setCustomizeOpen(true)} data-testid="quick-customize-open"
                className="hg-press flex items-center gap-1 text-[11px] font-semibold text-[#1A4331] hover:text-[#0F2D1F]">
                <Pencil className="w-3.5 h-3.5" /> {tr('nav.editAction')}
              </button>
            </div>
          )}
          <div className="relative" ref={wheelAnchorRef} onMouseEnter={openWheelSoon} onMouseLeave={closeWheelSoon}>
            <Link to={main.path} data-testid="nav-main-action"
              className={`hg-glow hg-press flex items-center rounded-2xl transition-all ${expanded ? 'gap-3 px-4 py-3.5 mb-2.5' : 'flex-col justify-center w-[60px] h-[60px] mx-auto mb-2'} ${isActive(main.path) ? 'bg-[#0F2D1F]' : 'bg-[#1A4331]'} text-[#FAF6EC] shadow-[0_12px_30px_-12px_rgba(26,67,49,0.85)]`}>
              <div className={`rounded-xl bg-[#B4D330]/18 flex items-center justify-center ${expanded ? 'w-10 h-10' : 'w-9 h-9'}`}>
                <MainIcon className={`${expanded ? 'w-6 h-6' : 'w-[22px] h-[22px]'} text-[#B4D330]`} />
              </div>
              {expanded && (
                <div className="min-w-0">
                  <p className="text-[15px] font-bold leading-tight truncate">{L(main.label)}</p>
                  <p className="text-[11px] text-[#B4D330]/80 font-semibold uppercase tracking-wide">Main · hold for more</p>
                </div>
              )}
            </Link>
            <button type="button" data-testid="main-action-wheel-toggle" onClick={toggleWheel} title="Quick actions"
              className={`hg-press absolute flex items-center justify-center text-[#B4D330] ${expanded ? 'right-2.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-lg hover:bg-[#B4D330]/15' : '-right-1 -top-1 w-5 h-5 rounded-full bg-[#0F2D1F] border border-[#B4D330]/45'}`}>
              <MoreHorizontal className={expanded ? 'w-4 h-4' : 'w-3 h-3'} />
            </button>
            {wheelOpen && (
              <div data-testid="main-action-wheel" onMouseEnter={cancelWheelClose} onMouseLeave={closeWheelSoon}
                style={{ position: 'fixed', top: wheelPos.top, left: wheelPos.left, zIndex: 60 }}
                className="w-60 bg-[#FAF6EC] rounded-2xl shadow-[0_20px_50px_-15px_rgba(15,45,31,0.5)] border border-[#1A4331]/10 p-2">
                <p className="text-[11px] font-semibold text-[#5C665C] uppercase tracking-wider px-2 py-1.5">Quick actions</p>
                {wheelActions.map((a) => {
                  const AIcon = a.Icon;
                  const cur = a.key === mainKey;
                  return (
                    <div key={a.key} className={`flex items-center gap-1 rounded-xl pl-2 pr-1 py-1 ${cur ? 'bg-[#1A4331]/[0.07]' : 'hover:bg-white/70'}`}>
                      <button type="button" data-testid={`wheel-go-${a.key}`} onClick={() => { setWheelOpen(false); pinMain(a.key); navigate(a.path); }}
                        className="flex items-center gap-2.5 flex-1 min-w-0 text-left py-1">
                        <span className="w-8 h-8 rounded-lg bg-[#1A4331] flex items-center justify-center shrink-0"><AIcon className="w-4 h-4 text-[#B4D330]" /></span>
                        <span className="text-[14px] font-semibold text-[#2D241E] truncate">{L(a.label)}</span>
                        {cur && <span className="text-[10px] font-bold text-[#6B9F4A] uppercase tracking-wide">Current</span>}
                      </button>
                      <button type="button" data-testid={`wheel-pin-${a.key}`} onClick={() => pinMain(a.key)} title="Set as default button"
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[#C9A85B]/15 shrink-0">
                        <Star className="w-4 h-4" style={{ color: cur ? '#C9A85B' : 'rgba(92,102,92,0.5)', fill: cur ? '#C9A85B' : 'none' }} />
                      </button>
                    </div>
                  );
                })}
                <p className="text-[10px] text-[#5C665C] px-2 pt-1.5 pb-0.5">Tap to jump · star to set your button</p>
              </div>
            )}
          </div>
          <div className={expanded ? 'grid grid-cols-3 gap-2' : 'space-y-2'}>
            {visibleTiles.map((k) => <QuickBtn key={k} tileKey={k} />)}
          </div>
        </div>

        {/* Stacked Hubs */}
        <nav className="flex-1 overflow-y-auto hg-scroll px-3 pt-1 pb-4 space-y-1 border-t border-[#1A4331]/10">
          {HUBS.filter((h) => !(h.gated && !intelOn)).map((hub) => {
            const HubIcon = hub.Icon;
            const open = expanded && openHubs[hub.key];
            const moreItems = (hub.more || []).filter((it) => !((it.path === '/seed-box' && !FEATURES.seedBox) || (it.path === '/pantry' && !FEATURES.pantry)));
            return (
              <div key={hub.key} className="pt-1">
                <button type="button" onClick={() => (expanded ? toggleHub(hub.key) : togglePin())}
                  data-testid={`hub-${hub.key}-toggle`} aria-expanded={open}
                  className={`hg-press w-full flex items-center rounded-2xl transition-all ${expanded ? 'gap-3 px-3 py-2.5 justify-between' : 'justify-center w-11 h-11 mx-auto'} text-[#1A4331] hover:bg-white/60`}>
                  <span className="flex items-center gap-3 min-w-0">
                    <HubIcon className="w-5 h-5 flex-shrink-0" />
                    {expanded && <span className="text-[13px] font-bold uppercase tracking-wider truncate">{L(hub.label)}</span>}
                  </span>
                  {expanded && (open ? <ChevronDown className="w-4 h-4 text-[#5C665C]" /> : <ChevronRight className="w-4 h-4 text-[#5C665C]" />)}
                </button>
                {open && (
                  <div className="mt-1 space-y-0.5" data-testid={`hub-${hub.key}-items`}>
                    {hub.items.map((item) => <HubItem key={item.path} item={item} />)}
                    {moreItems.length > 0 && (
                      <>
                        <button type="button" onClick={() => toggleMore(hub.key)} data-testid={`hub-${hub.key}-more-toggle`}
                          className="hg-press w-full flex items-center gap-1.5 pl-11 pr-3 py-2 rounded-xl text-[#5C665C] hover:bg-white/60 hover:text-[#1A4331]">
                          {moreOpen[hub.key] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <span className="text-[13px] font-semibold">{moreOpen[hub.key] ? 'Fewer tools' : `More ${hub.label.toLowerCase()} tools`}</span>
                        </button>
                        {moreOpen[hub.key] && moreItems.map((item) => <HubItem key={item.path} item={item} />)}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {!intelOn && expanded && (
            <Link to="/settings" data-testid="intelligence-off-note" className="flex items-center gap-3 px-3 py-3 rounded-xl text-[#8EAA97] hover:bg-white/60">
              <Cpu className="w-5 h-5" />
              <span className="font-medium text-sm">{tr('nav.intelligenceOff')}</span>
            </Link>
          )}
        </nav>

        {/* Footer: user + notifications + settings + logout */}
        <div className={`border-t border-[#1A4331]/10 flex items-center ${expanded ? 'px-3 py-3 gap-2' : 'flex-col py-3 gap-2'}`}>
          <div className="w-9 h-9 rounded-full overflow-hidden bg-[#8EAA97] flex-shrink-0">
            {user?.picture && !avatarError ? (
              <img src={user.picture} alt={user.name} className="w-full h-full object-cover" onError={() => setAvatarError(true)} />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white text-sm font-semibold">{(user?.name?.[0] || '?').toUpperCase()}</div>
            )}
          </div>
          {expanded && <span className="text-sm font-medium text-[#2D241E] truncate flex-1">{user?.name}</span>}
          <Link to="/messages" data-testid="rail-messages" className="hg-press relative w-9 h-9 rounded-full hover:bg-white/60 flex items-center justify-center">
            <MessageCircle className="w-[18px] h-[18px] text-[#5C665C]" />
            {msgUnread > 0 && (
              <span className="absolute top-0.5 right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-[#C66B4A] text-white text-[9px] font-bold flex items-center justify-center">{msgUnread > 9 ? '9+' : msgUnread}</span>
            )}
          </Link>
          <Link to="/notifications" data-testid="rail-notifications" className="hg-press relative w-9 h-9 rounded-full hover:bg-white/60 flex items-center justify-center">
            <Bell className="w-[18px] h-[18px] text-[#5C665C]" />
            {unread > 0 && (
              <span className="absolute top-0.5 right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-[#E2725B] text-white text-[9px] font-bold flex items-center justify-center">{unread > 9 ? '9+' : unread}</span>
            )}
          </Link>
          <Link to="/settings" data-testid="settings-link" className="hg-press w-9 h-9 rounded-full hover:bg-white/60 flex items-center justify-center"><Settings className="w-[18px] h-[18px] text-[#5C665C]" /></Link>
          <button onClick={handleLogout} data-testid="logout-button" className="hg-press w-9 h-9 rounded-full hover:bg-[#C66B4A]/12 flex items-center justify-center"><LogOut className="w-[18px] h-[18px] text-[#5C665C]" /></button>
        </div>
      </aside>

      {/* ─── QUICK ACTIONS CUSTOMIZE MODAL ─── */}
      <AnimatePresence>
        {customizeOpen && (
          <motion.div className="hidden md:flex fixed inset-0 z-[70] items-center justify-center bg-[#0F2D1F]/40 backdrop-blur-sm p-6"
            data-testid="quick-customize-modal"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCustomizeOpen(false)}>
            <motion.div className="hg-glass rounded-3xl w-full max-w-lg max-h-[82vh] overflow-y-auto hg-scroll p-6"
              initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 8 }}
              onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{tr('nav.customizeActions')}</h3>
                <button onClick={() => setCustomizeOpen(false)} data-testid="quick-customize-close"
                  className="hg-press w-9 h-9 rounded-full bg-white/70 hover:bg-white flex items-center justify-center">
                  <X className="w-5 h-5 text-[#5C665C]" />
                </button>
              </div>
              <p className="text-sm text-[#5C665C] mb-4">Add as many tiles as you like, then reorder them. Your main action button and hubs stay put.</p>

              <p className="text-[11px] font-semibold text-[#5C665C] uppercase tracking-wider mb-2">Your tiles ({visibleTiles.length})</p>
              <div className="space-y-2 mb-5">
                {quickTiles.map((k, i) => {
                  const t = QUICK_TILE_POOL[k];
                  if (!t) return null;
                  const Icon = t.icon;
                  return (
                    <div key={k} data-testid={`qtile-row-${k}`} className="flex items-center gap-3 bg-white/70 rounded-2xl px-3 py-2.5">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${t.accent ? 'bg-[#C9A85B]/25 text-[#7A5E1F]' : 'bg-[#1A4331]/8 text-[#1A4331]'}`}>
                        <Icon className="w-[18px] h-[18px]" />
                      </div>
                      <span className="flex-1 font-semibold text-[14px] text-[#1A4331] truncate">{L(t.label)}</span>
                      <button onClick={() => moveTile(k, -1)} disabled={i === 0} data-testid={`qtile-up-${k}`}
                        className="hg-press w-8 h-8 rounded-lg hover:bg-[#1A4331]/8 flex items-center justify-center disabled:opacity-30">
                        <ArrowUp className="w-4 h-4 text-[#5C665C]" />
                      </button>
                      <button onClick={() => moveTile(k, 1)} disabled={i === quickTiles.length - 1} data-testid={`qtile-down-${k}`}
                        className="hg-press w-8 h-8 rounded-lg hover:bg-[#1A4331]/8 flex items-center justify-center disabled:opacity-30">
                        <ArrowDown className="w-4 h-4 text-[#5C665C]" />
                      </button>
                      <button onClick={() => removeTile(k)} data-testid={`qtile-remove-${k}`}
                        className="hg-press w-8 h-8 rounded-lg bg-[#C66B4A]/12 hover:bg-[#C66B4A]/20 flex items-center justify-center">
                        <X className="w-4 h-4 text-[#C66B4A]" />
                      </button>
                    </div>
                  );
                })}
                {visibleTiles.length === 0 && <p className="text-sm text-[#7A6B57]">No tiles yet — add some below.</p>}
              </div>

              <p className="text-[11px] font-semibold text-[#5C665C] uppercase tracking-wider mb-2">Add a tile</p>
              <div className="grid grid-cols-3 gap-2">
                {addableTiles.map((k) => {
                  const t = QUICK_TILE_POOL[k];
                  const Icon = t.icon;
                  return (
                    <button key={k} onClick={() => addTile(k)} data-testid={`qtile-add-${k}`}
                      className="hg-press flex flex-col items-center gap-1.5 bg-white/60 hover:bg-white rounded-2xl px-2 py-3 text-[#1A4331]">
                      <div className="relative">
                        <Icon className="w-5 h-5" />
                        <span className="absolute -top-1.5 -right-2 w-3.5 h-3.5 rounded-full bg-[#1A4331] flex items-center justify-center">
                          <Plus className="w-2.5 h-2.5 text-[#B4D330]" />
                        </span>
                      </div>
                      <span className="text-[11px] font-semibold leading-tight text-center truncate max-w-full">{L(t.label)}</span>
                    </button>
                  );
                })}
                {addableTiles.length === 0 && <p className="col-span-3 text-sm text-[#7A6B57]">Every tile is already in your bar.</p>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── DESKTOP RIGHT COACH DOCK + toggle ─── */}
      {showCoach && <CoachDock user={user} open={coachOpen} onClose={() => setCoach(false)} />}
      {showCoach && !coachOpen && (
        <button onClick={() => setCoach(true)} data-testid="coach-dock-toggle"
          className="hg-glow hg-press hidden md:flex fixed right-0 top-1/2 -translate-y-1/2 z-30 flex-col items-center gap-1 bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] px-3 py-4 rounded-l-2xl shadow-[0_12px_30px_-12px_rgba(26,67,49,0.8)]">
          <Sparkles className="w-5 h-5 text-[#C9A85B]" />
          <span className="text-[10px] font-bold uppercase tracking-wider">Coach</span>
        </button>
      )}

      {/* ─── MAIN CANVAS ─── */}
      <main className={`pb-24 md:pb-10 pt-6 transition-[margin] duration-300 ${railPinned ? 'md:ml-72' : 'md:ml-[76px]'} ${showCoach && coachOpen ? 'lg:mr-[360px]' : ''}`}>
        <div className="px-5 md:px-8 xl:px-10">
          {intelOn && (
            <div className="hidden md:block">
              {member ? <VitalsHud /> : (
                <PremiumGate user={user} compact title="Garden Intelligence — Premium" testId="vitals-premium-gate">
                  <VitalsHud />
                </PremiumGate>
              )}
            </div>
          )}
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 28 }}
          >
            {children}
          </motion.div>
        </div>
      </main>
    </div>
  );
}

export default Layout;
